import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { injectToastContext, NgpToast, NgpToastManager } from 'ng-primitives/toast';
import { twMerge } from 'tailwind-merge';

import { I18nPipe } from '@core/i18n';

import { ButtonComponent } from '@shared/button';
import { IconComponent, type IconName } from '@shared/icon';

import {
  toastContent,
  toastSubtitle,
  toastSuffixIcon,
  toastTextWrapper,
  toastTitle,
} from './toast.cva';
import { type ToastContext } from './toast.types';

@Component({
  selector: 'ml-toast',
  imports: [IconComponent, ButtonComponent, I18nPipe],
  hostDirectives: [NgpToast],
  host: {
    '[class]': 'hostClass()',
  },
  templateUrl: './toast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent {
  readonly extraClass = input<string>('', { alias: 'class' });
  private readonly toastManager = inject(NgpToastManager);
  private readonly toast = inject(NgpToast);
  protected readonly ctx = injectToastContext<ToastContext & { dismissible?: boolean }>();

  protected readonly hasTitle = computed(() => (this.ctx.title ? true : false));
  protected readonly contentClasses = computed(() => toastContent({ variant: this.ctx.variant }));
  protected readonly textWrapperClasses = computed(() => toastTextWrapper());
  protected readonly titleClasses = computed(() => toastTitle({ hasTitle: this.hasTitle() }));
  protected readonly subtitleClasses = computed(() => toastSubtitle({ hasTitle: this.hasTitle() }));
  protected readonly hostClass = computed(() => twMerge('toast group/toast', this.extraClass()));
  readonly isCloseIcon = computed(() => this.ctx.suffixIcon === 'close');
  protected readonly suffixIconClasses = computed(() =>
    toastSuffixIcon({ close: this.isCloseIcon() }),
  );

  protected dismiss(): void {
    void this.toastManager.dismiss(this.toast);
  }

  protected onIconClick(icon: IconName): void {
    if (icon === 'close') {
      void this.toastManager.dismiss(this.toast);
      return;
    }

    this.ctx.events?.next({
      type: 'icon',
      icon,
    });
  }

  protected onActionClick(): void {
    this.ctx.events?.next({
      type: 'action',
      action: this.ctx.actionId ?? '',
    });

    void this.toastManager.dismiss(this.toast);
  }
}
